/**
 * Performance monitoring utility for MultiCalendar and its children.
 *
 * HOW TO USE:
 *   - Set CALENDAR_PERF_MONITOR_ENABLED = true to enable (default: true)
 *   - Open the browser DevTools console and interact with the calendar
 *   - Each render cycle logs a table sorted by total time spent
 *   - Set CALENDAR_PERF_MONITOR_ENABLED = false to disable with zero overhead
 */

export const CALENDAR_PERF_MONITOR_ENABLED = false;

interface Measurement {
  totalMs: number;
  callCount: number;
}

class CalendarPerfMonitorClass {
  private measurements = new Map<string, Measurement>();

  /** Wraps a function call and accumulates its execution time under `label`. */
  measure<T>(label: string, fn: () => T): T {
    if (!CALENDAR_PERF_MONITOR_ENABLED) return fn();
    const t0 = performance.now();
    const result = fn();
    const elapsed = performance.now() - t0;
    const existing = this.measurements.get(label);
    if (existing) {
      existing.totalMs += elapsed;
      existing.callCount++;
    } else {
      this.measurements.set(label, { totalMs: elapsed, callCount: 1 });
    }
    return result;
  }

  /** Accumulates raw elapsed time under `label` (useful when you already know the delta). */
  accumulate(label: string, elapsedMs: number): void {
    if (!CALENDAR_PERF_MONITOR_ENABLED) return;
    const existing = this.measurements.get(label);
    if (existing) {
      existing.totalMs += elapsedMs;
      existing.callCount++;
    } else {
      this.measurements.set(label, { totalMs: elapsedMs, callCount: 1 });
    }
  }

  /**
   * Wraps every modifier function so each call is timed under `modifier::<key>`.
   * When disabled, returns the original object reference (no overhead, no useMemo cache bust).
   */
  wrapModifiers<T extends Record<string, (day: any) => any>>(modifiers: T): T {
    if (!CALENDAR_PERF_MONITOR_ENABLED) return modifiers;
    const wrapped: any = {};
    for (const [key, fn] of Object.entries(modifiers)) {
      wrapped[key] = (day: any) => this.measure(`modifier::${key}`, () => fn(day));
    }
    return wrapped as T;
  }

  /** Clears all accumulated measurements (call at the start of each render cycle). */
  reset(): void {
    if (!CALENDAR_PERF_MONITOR_ENABLED) return;
    this.measurements.clear();
  }

  /**
   * Prints a sorted table of all accumulated measurements and resets the monitor.
   * Call this from componentDidMount / componentDidUpdate (after all children rendered).
   */
  report(title = 'Calendar Performance Report'): void {
    if (!CALENDAR_PERF_MONITOR_ENABLED) return;
    if (this.measurements.size === 0) return;

    const sorted = Array.from(this.measurements.entries()).sort(([, a], [, b]) => b.totalMs - a.totalMs);

    console.group(`%c[PerfMonitor] ${title}`, 'color:#00aaff;font-weight:bold');
    console.table(
      Object.fromEntries(
        sorted.map(([name, { totalMs, callCount }]) => [
          name,
          {
            'Total (ms)': +totalMs.toFixed(3),
            Calls: callCount,
            'Avg/call (ms)': +(totalMs / callCount).toFixed(4),
          },
        ]),
      ),
    );
    console.groupEnd();

    this.measurements.clear();
  }
}

/** Singleton — shared across MultiCalendar and its children. */
export const CalendarPerfMonitor = new CalendarPerfMonitorClass();
